import * as React from 'react';
import { Link } from 'react-router-dom';
import { History } from 'lucide-react';
import { Avatar, Button, EmptyState, ErrorState, SkeletonRow, SlideOver } from '@/components/ui';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';

export interface RecentActivity {
  id: string;
  action: string;
  createdAt: string;
  user: { firstName: string; lastName: string } | null;
  ticket: { id: string; ticketNumber: number; subject: string };
}

type Translator = ReturnType<typeof useI18n>['t'];

function formatRelative(iso: string, t: Translator): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return t('common.justNow');
  if (minutes < 60) return t('common.minutesAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('common.hoursAgo', { count: hours });
  const days = Math.round(hours / 24);
  return t('common.daysAgo', { count: days });
}

function describeActivity(activity: RecentActivity, t: Translator): React.ReactNode {
  const actorName = activity.user ? `${activity.user.firstName} ${activity.user.lastName}` : t('activities.systemActor');
  const verb = activity.action.replace(/_/g, ' ');
  return (
    <>
      <span className="font-semibold text-ink">{actorName}</span>{' '}
      <span className="text-ink-muted">{verb}</span>{' '}
      <Link to={`/tickets/${activity.ticket.id}`} className="font-medium text-brand-700 hover:underline">
        {activity.ticket.subject} (#{activity.ticket.ticketNumber})
      </Link>
    </>
  );
}

interface RecentActivitiesPanelProps {
  groupId?: string;
}

export function RecentActivitiesButton({ groupId }: RecentActivitiesPanelProps) {
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [activities, setActivities] = React.useState<RecentActivity[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    api
      .get<{ data: RecentActivity[] }>('/dashboard/recent-activities', { params: { ...(groupId ? { groupId } : {}), limit: 30 }, signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setActivities(response.data);
      })
      .catch((caught) => {
        if (!(caught instanceof Error && caught.name === 'AbortError')) setError(t('activities.loadError'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, groupId, t]);

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <History className="mr-2 h-4 w-4" aria-hidden="true" />
        {t('activities.button')}
      </Button>
      <SlideOver
        open={open}
        onClose={() => setOpen(false)}
        title={<span className="flex items-center gap-2"><History className="h-4 w-4" aria-hidden="true" />{t('activities.title')}</span>}
      >
        {loading ? (
          <SkeletonRow rows={6} />
        ) : error ? (
          <ErrorState message={error} className="m-5" />
        ) : activities.length === 0 ? (
          <EmptyState
            title={t('activities.noneTitle')}
            description={t('activities.noneDescription')}
          />
        ) : (
          <ul className="divide-y">
            {activities.map((activity) => (
              <li key={activity.id} className="flex gap-3 px-5 py-3">
                <Avatar
                  name={activity.user ? `${activity.user.firstName} ${activity.user.lastName}` : t('activities.systemActor')}
                  size="sm"
                  className="mt-1"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-snug">{describeActivity(activity, t)}</p>
                  <p className="mt-0.5 text-xs text-ink-subtle">{formatRelative(activity.createdAt, t)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SlideOver>
    </>
  );
}
